"use client";

import { memo } from "react";
import { useTranslations } from "next-intl";
import { Star, Trophy, Users as UsersIcon, AlertTriangle } from "lucide-react";

import { Link } from "@/i18n/navigation";
import { OrganizationData } from "@/entities/organization/model/types";
import { useOrganizationRestriction } from "@/hooks/use-organization-restriction";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { CardBanner } from "@/shared/ui/card-banner";

interface OrganizationCardProps {
  organization: OrganizationData;
}

export const OrganizationCard = memo(
  ({ organization: org }: OrganizationCardProps) => {
    const tUi = useTranslations("Ui");
    const { isRestricted } = useOrganizationRestriction(org.id);

    return (
      <Link
        href={`/organizations/${org.url || org.id}`}
        className="flex h-full flex-col"
      >
        <Card className="group flex h-full flex-col gap-0 p-0 overflow-hidden border border-border/60 bg-card/70 transition-all duration-200 hover:border-primary/40 hover:shadow-lg">
          <CardBanner
            src={org.banner}
            alt={org.name}
            className="bg-muted"
            overlayClassName="bg-black/40"
          />

          <CardContent className="flex h-full flex-col gap-4 p-4 pt-5">
            <div className="flex-1 space-y-4">
              {/* Header: Name & Verification */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <h3 className="text-xl font-bold leading-tight tracking-tight text-foreground line-clamp-2">
                    {org.name}
                  </h3>
                  {isRestricted && (
                    <span title={tUi("organizationCard.uchastieOgranicheno")}>
                      <AlertTriangle
                        className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0"
                        aria-label={tUi("organizationCard.uchastieOgranicheno")}
                      />
                    </span>
                  )}
                </div>
                {org.isVerified && (
                  <Star className="h-5 w-5 shrink-0 text-yellow-500 fill-yellow-500 mt-0.5" />
                )}
              </div>
              {isRestricted && (
                <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                  {tUi("organizationCard.uchastieOgranicheno")}
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {org.description || tUi("litOrganizationCard.opisanieOtsutstvuet")}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-medium text-muted-foreground">
                <div className="rounded-lg border border-border/60 bg-muted/40 px-2 py-2">
                  <UsersIcon className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                  <div className="text-sm font-semibold text-foreground">
                    {org.memberCount}
                  </div>
                  <div>{tUi("organizationCard.uchastnikov")}</div>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/40 px-2 py-2">
                  <Trophy className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                  <div className="text-sm font-semibold text-foreground">
                    {org.stats.totalTournaments}
                  </div>
                  <div>{tUi("organizationCard.turnirov")}</div>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/40 px-2 py-2">
                  <div className="mx-auto mb-1 h-4 w-4 text-muted-foreground">
                    ★
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    {org.subscriberCount ?? 0}
                  </div>
                  <div>{tUi("organizationCard.podpischikov")}</div>
                </div>
              </div>
            </div>

            {org.upcomingTournaments && org.upcomingTournaments.length > 0 && (
              <div className="px-0 pb-2">
                <div className="p-3 bg-primary/5 rounded border-l-2 border-primary">
                  <p className="text-xs text-primary font-medium mb-1">
                    {tUi("organizationCard.blizhayshiyTurnir")}
                  </p>
                  <p className="text-sm font-semibold line-clamp-1">
                    {org.upcomingTournaments[0]?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {org.upcomingTournaments[0]?.startDate ??
                      tUi("litOrganizationCard.dataNeNaznachena")}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
    );
  },
);

OrganizationCard.displayName = "OrganizationCard";
